import { useEffect, useRef, useState } from 'react';

/**
 * Returns a ref and a boolean that flips to `true` once the referenced
 * element has positive clientWidth and clientHeight.
 *
 * Useful for deferring Recharts rendering until the container is laid out,
 * preventing the "width(-1) and height(-1)" console warning.
 */
export function useContainerReady<T extends HTMLElement = HTMLDivElement>() {
  const ref = useRef<T>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (el.clientWidth > 0 && el.clientHeight > 0) {
      setReady(true); // eslint-disable-line react-hooks/set-state-in-effect -- reads the measured DOM, which only exists after mount
      return;
    }
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.contentRect.width > 0 && entry.contentRect.height > 0) {
          setReady(true);
          ro.disconnect();
          return;
        }
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return { ref, ready };
}
